'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["position"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { CtasIcon } from 'foundations-assets';
import { Button } from 'foundations-components/transitional/components/Button';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FileAttachment } from 'foundations-components/transitional/components/FileAttachment';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { Small } from 'foundations-components/transitional/components/Small';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { View } from 'foundations-components/transitional/components/View';
import { getFullUrl } from 'hubspot-url-utils';
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import { VideoOverlayPositions, VideoOverlayTypes } from '../../constants/video';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import ConversionAssetOptionsPanel from './ConversionAssetOptionsPanel';
import ConversionAssetSummary from './ConversionAssetSummary';
import VideoFormSelect from './VideoFormSelect';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEFAULT_OVERLAY_POSITION = VideoOverlayPositions.AFTER;
const renderOverlaySelect = ({
  overlayType,
  overlayGuid,
  overlayName,
  disabled,
  onOpenPicker,
  onUpdateConversionAsset,
  onRemoveOverlay,
  formInfo,
  fetchFormInfo,
  fetchForms,
  trackVideoInteraction
}) => {
  const useFormSelect = overlayType === VideoOverlayTypes.FORM;
  const handleOpenPicker = () => {
    if (overlayType === VideoOverlayTypes.FORM) {
      window.open(`${getFullUrl('app')}/forms/${PortalIdParser.get()}`);
    } else {
      onOpenPicker();
    }
  };
  if (useFormSelect) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: `RichTextLib.video.overlay.${overlayType}.label`,
        linkText: 'RichTextLib.video.overlay.createOrEditForm',
        onLinkClick: handleOpenPicker,
        required: true
      }), /*#__PURE__*/_jsx(VideoFormSelect, {
        formInfo: formInfo,
        selectedGuid: overlayGuid,
        fetchForms: fetchForms,
        readOnly: false,
        onChange: id => {
          fetchFormInfo(id);
          onUpdateConversionAsset({
            type: VideoOverlayTypes.FORM,
            id,
            position: DEFAULT_OVERLAY_POSITION
          });
        },
        trackVideoInteraction: trackVideoInteraction
      })]
    });
  }
  if (overlayGuid) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: `RichTextLib.video.overlay.${overlayType}.label`,
        linkText: 'RichTextLib.video.overlay.replace',
        onLinkClick: handleOpenPicker,
        required: true
      }), /*#__PURE__*/_jsx(FileAttachment, {
        fileName: overlayName || '...',
        closeable: true,
        size: "small",
        onCloseClick: onRemoveOverlay,
        renderPreview: /*#__PURE__*/_jsx(CtasIcon, {})
      })]
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: unescapedText(`RichTextLib.video.overlay.${overlayType}.label`),
    required: true,
    marginBlockStart: "100",
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "insert-overlay-button",
      use: "tertiary-light",
      disabled: disabled,
      size: "small",
      onClick: handleOpenPicker,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `RichTextLib.video.overlay.${overlayType}.button`
      })
    })
  });
};
const renderRadioOption = props => {
  const {
      position
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(RadioInput, Object.assign({}, rest, {
    "data-test-id": `overlay-position-${position}`,
    name: `display${position}`,
    value: position,
    size: "small",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `RichTextLib.video.overlay.display.${position}`
    })
  }));
};
const propTypes = {
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  conversionAsset: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.string,
    position: PropTypes.string
  }),
  ctaName: PropTypes.string,
  formInfo: PropTypes.object,
  onUpdateConversionAsset: PropTypes.func.isRequired,
  onOpenConversionAssetPanel: PropTypes.func,
  onOpenCtaPicker: PropTypes.func.isRequired,
  updateCtaInfo: PropTypes.func.isRequired,
  updateFormInfo: PropTypes.func.isRequired,
  clearCtaInfo: PropTypes.func.isRequired,
  clearFormInfo: PropTypes.func.isRequired,
  fetchFormInfo: PropTypes.func.isRequired,
  fetchForms: PropTypes.func.isRequired,
  trackVideoInteraction: PropTypes.func,
  useConversionAssetOptionsPanel: PropTypes.bool,
  video: PropTypes.shape({
    videoId: PropTypes.number,
    duration: PropTypes.number
  })
};
function VideoPopoverOverlayControls(props) {
  const {
    dropdownClassName,
    conversionAsset,
    onUpdateConversionAsset,
    onOpenCtaPicker,
    onOpenConversionAssetPanel,
    updateCtaInfo,
    clearCtaInfo,
    clearFormInfo,
    ctaName,
    formInfo: _formInfo = {},
    fetchFormInfo,
    fetchForms,
    useConversionAssetOptionsPanel,
    trackVideoInteraction = () => {},
    video
  } = props;
  const [isOverlayOptionsPanelOpen, setIsOverlayOptionsPanelOpen] = useState(false);
  const {
    type: overlayType = VideoOverlayTypes.NONE,
    id: overlayGuid,
    position: overlayPosition
  } = conversionAsset || {};
  let overlayName;
  if (overlayType === VideoOverlayTypes.CTA) {
    overlayName = ctaName;
  } else if (overlayType === VideoOverlayTypes.FORM) {
    overlayName = _formInfo.name;
  }
  const overlayOptions = Object.values(VideoOverlayTypes).map(type => ({
    text: I18n.text(`RichTextLib.video.overlay.${type}.option`),
    value: type
  }));
  const handleOpenPicker = guid => {
    if (overlayType === VideoOverlayTypes.CTA) {
      onOpenCtaPicker(guid, (updatedGuid, data) => {
        updateCtaInfo(data);
        onUpdateConversionAsset({
          type: overlayType,
          id: updatedGuid,
          position: DEFAULT_OVERLAY_POSITION
        });
      });
    }
  };
  const handleOverlayTypeChange = evt => {
    const {
      value
    } = evt.target;
    let updatedConversionAsset = null;
    if (value !== VideoOverlayTypes.NONE) {
      updatedConversionAsset = {
        type: value
      };
    }
    onUpdateConversionAsset(updatedConversionAsset);
    clearCtaInfo();
    clearFormInfo();
    trackVideoInteraction(`select-overlay-type-${value.toLowerCase()}`);
  };
  const handleOverlayPositionChange = evt => {
    const {
      value
    } = evt.target;
    onUpdateConversionAsset(Object.assign({}, conversionAsset, {
      position: value
    }));
  };
  const handleRemoveOverlay = () => {
    onUpdateConversionAsset({
      type: overlayType
    });
    clearCtaInfo();
    clearFormInfo();
  };
  const handleEditConversionAssetOptions = () => {
    if (onOpenConversionAssetPanel) {
      onOpenConversionAssetPanel(video, conversionAsset, updatedAsset => {
        onUpdateConversionAsset(updatedAsset);
      });
    } else {
      setIsOverlayOptionsPanelOpen(true);
    }
  };
  const renderDisplayOptions = () => {
    if (!useConversionAssetOptionsPanel) {
      return /*#__PURE__*/_jsx(FormControl, {
        label: I18n.text('RichTextLib.video.overlay.display.label'),
        marginBlockStart: "100",
        children: /*#__PURE__*/_jsx(ToggleGroup, {
          name: "videoOverlayDisplayOptions",
          children: Object.values(VideoOverlayPositions).map(position => renderRadioOption({
            key: position,
            position,
            checked: overlayPosition === position,
            disabled: !overlayGuid,
            onClick: handleOverlayPositionChange
          }))
        })
      });
    }
    if (!(conversionAsset && conversionAsset.type && conversionAsset.id)) {
      return null;
    }
    return /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "200",
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: 'RichTextLib.video.overlay.display.labelWithOptionsPanel',
        linkText: 'RichTextLib.video.conversionAssetSummary.edit',
        onLinkClick: handleEditConversionAssetOptions,
        "data-test-id": 'edit-conversion-asset-options'
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(ConversionAssetSummary, {
          paddingInlineStart: "600",
          conversionAsset: conversionAsset
        })
      })]
    });
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(Fieldset, {
      paddingInline: "500",
      paddingBlockEnd: "100",
      _size: "small",
      children: [/*#__PURE__*/_jsx(FormControl, {
        label: unescapedText(useConversionAssetOptionsPanel ? 'RichTextLib.video.overlay.labelWithOptionsPanel' : 'RichTextLib.video.overlay.label'),
        children: /*#__PURE__*/_jsx(Select, {
          "data-test-id": "video-overlay-select",
          dropdownClassName: dropdownClassName,
          value: overlayType,
          options: overlayOptions,
          defaultValue: VideoOverlayTypes.NONE,
          searchable: false,
          onChange: handleOverlayTypeChange
        })
      }), renderOverlaySelect({
        overlayType,
        overlayGuid,
        overlayName,
        disabled: overlayType === VideoOverlayTypes.NONE,
        formInfo: _formInfo,
        fetchForms,
        fetchFormInfo,
        useConversionAssetOptionsPanel,
        onOpenPicker: handleOpenPicker,
        onRemoveOverlay: handleRemoveOverlay,
        onUpdateConversionAsset,
        trackVideoInteraction
      }), renderDisplayOptions()]
    }), isOverlayOptionsPanelOpen && /*#__PURE__*/_jsx(ConversionAssetOptionsPanel, {
      conversionAsset: conversionAsset,
      video: video,
      onClose: () => setIsOverlayOptionsPanelOpen(false),
      onSubmit: asset => {
        onUpdateConversionAsset(asset);
        setIsOverlayOptionsPanelOpen(false);
      },
      trackVideoInteraction: trackVideoInteraction
    })]
  });
}
VideoPopoverOverlayControls.propTypes = propTypes;
VideoPopoverOverlayControls.displayName = 'VideoPopoverOverlayControls';
export default VideoPopoverOverlayControls;